"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBatchUpdateProductAttributes = exports.useFindVariantsOfAttribute = exports.useFindMatchingVariant = exports.useSelectionStore = exports.useAddProducts = exports.useAddProductsToCart = exports.useAddToCartData = exports.useUpdateProductAttributes = exports.useSelectionPricing = exports.useProductPricing = exports.useVariantsData = exports.useProductsData = exports.useProductData = exports.useProductsState = exports.useProductState = exports.useAttributeOptions = void 0;
/* eslint-disable */
/* TODO: fix ALL linting errors as some may call out code smells */
const react_1 = require("react");
const shallow_1 = require("zustand/shallow");
const zustand_1 = require("zustand");
const get_1 = __importDefault(require("lodash/get"));
const pick_1 = __importDefault(require("lodash/pick"));
const selection_store_1 = require("./selection.store");
const pattern_library_modules_promotions_1 = require("@saatva-bits/pattern-library.modules.promotions");
const selection_utils_1 = require("./selection.utils");
const selection_context_1 = require("./selection.context");
const useSelectionStore = (selector, shallow) => {
    const contextSelectionStore = (0, react_1.useContext)(selection_context_1.StoreContext);
    // This allows using the selection hooks outside of the Selection Context
    const store = typeof contextSelectionStore === 'undefined' ? selection_store_1.selectionStore : contextSelectionStore;
    return (0, zustand_1.useStore)(store, selector, shallow);
};
exports.useSelectionStore = useSelectionStore;
/**
 * Returns function that adds the products to the seletion store, uses the initializeStore under the hood
 * said function takes the productArray and maps it to an object of productCodes for easy and efficient lookup
 * @returns {(
 *   productsData: import('./selection.types').Product[],
 *   pageDefinedAttributes: import('./selection.types').AttributeValue,
 *   userDefinedAttributes: import('./selection.types').AttributeValue,
 *   productOverrides: import('./selection.types').AttributeValue
 * ) => {
*  productsData: Object.<string, import('./selection.types').Product>,
*  productState: Object.<string, import('./selection.types').Variant>
* }}
 */
const useAddProducts = () => {
    return useSelectionStore((state) => state.initializeStore);
};
exports.useAddProducts = useAddProducts;
/**
 * Returns the specified attributes for the requested product from the selection store
 * Data returned corresponds to the currently matching variant for that product, and will only return
 * the passed attributes
 * @param {string} productCode
 * @param {Array<import('./selection.types.js').attributeCode>} attributes
 * @returns {import('./selection.types.js').Variant}
 */
const useProductState = (productCode, attributes = []) => {
    const selector = (0, react_1.useCallback)((state) => {
        const productState = (0, get_1.default)(state, [selection_store_1.STATE_KEY, productCode]);
        return attributes.length ? (0, pick_1.default)(productState, attributes) : productState;
    }, [productCode, ...attributes]);
    return useSelectionStore(selector, shallow_1.shallow);
};
exports.useProductState = useProductState;
/**
 * Same as useProductState, but retrieves variant data for multiple product codes, returning an array
 * @param {string[]} productCodes
 * @param {Array<import('./selection.types.js').Attribute>} attributes
 * @returns {import('./selection.types.js').Variant[]}
 */
const useProductsState = (productCodes, attributes = []) => {
    const selector = (0, react_1.useCallback)((state) => {
        return productCodes.map((productCode) => {
            const productState = (0, get_1.default)(state, [selection_store_1.STATE_KEY, productCode]);
            return attributes.length ? (0, pick_1.default)(productState, attributes) : productState;
        });
    }, [productCodes, ...attributes]);
    return useSelectionStore(selector, shallow_1.shallow);
};
exports.useProductsState = useProductsState;
/**
 * Returns the product data saved in the selections store
 * @param {string} productCode
 * @returns {import('./selection.types').Product}
 */
const useProductData = (productCode) => {
    const selector = (0, react_1.useCallback)((state) => (0, get_1.default)(state, [selection_store_1.DATA_KEY, productCode]), [productCode]);
    return useSelectionStore(selector);
};
exports.useProductData = useProductData;
/**
 * Same as useProductData, but retrieves product data for multiple product codes, returning an array
 * @param {string[]} productCodes
 * @returns {import('./selection.types.js').Product[]}
 */
const useProductsData = (productCodes) => {
    const selector = (0, react_1.useCallback)((state) => {
        return productCodes.map((productCode) => {
            return (0, get_1.default)(state, [selection_store_1.DATA_KEY, productCode]);
        });
    }, [productCodes]);
    return useSelectionStore(selector, shallow_1.shallow);
};
exports.useProductsData = useProductsData;
/**
 * Returns a list of variants matching the provided list of codes and skus from the selection Data store
 * @param {{productCode: string, sku: string}[]} codesAndSkus
 * @returns {import('./selection.types.js').Variant[]}
 */
const useVariantsData = (codesAndSkus = []) => {
    if (!codesAndSkus)
        codesAndSkus = [];
    // Create a list of product codes from the variants in order to get the product data
    const productCodes = codesAndSkus.map(codeAndSku => { return codeAndSku.productCode; });
    const productData = useProductsData(productCodes);
    // For each product matching a product code in the list of codes and skus
    return productData.map((product, i) => {
        // Find the product's variant which matches the sku provided in the list of codes and skus
        return product.variants.find(variant => {
            return variant.sku === codesAndSkus[i].sku;
        });
    });
};
exports.useVariantsData = useVariantsData;
const _useAttributeOptionsData = (productCode, attributeCode) => {
    const selector = (0, react_1.useCallback)((state) => (0, get_1.default)(state, [selection_store_1.DATA_KEY, productCode, 'options', attributeCode]), [productCode, attributeCode]);
    return useSelectionStore(selector);
};
/**
 * Returns function that updates the corresponding 'state' for a product
 *
 * This is selecting the matching variant to the users attributes selections (size, swatch)
 * @returns {(productCode: string, updatedAttributes: import('./selection.types').AttributeValue) => void}
 */
const useUpdateProductAttributes = () => {
    return useSelectionStore((state) => state.updateProductAttributes);
};
exports.useUpdateProductAttributes = useUpdateProductAttributes;
/**
 * Returns function that updates the corresponding 'state' for a product
 *
 * This is selecting the matching variant to the users attributes selections (size, swatch)
 * @returns {(productUpdates: Record<string, Record<string, string>>) => void} Function that accepts an object
 * where keys are product codes and values are objects containing the swatch attribute updates
 */
const useBatchUpdateProductAttributes = () => {
    return useSelectionStore((state) => state.batchUpdateProductAttributes);
};
exports.useBatchUpdateProductAttributes = useBatchUpdateProductAttributes;
const _useHiddenAttributeValues = (productCode, attributeCode) => {
    const selector = (0, react_1.useCallback)((state) => (0, get_1.default)(state, [selection_store_1.STATE_KEY, productCode, 'hiddenAttributeValues', attributeCode]), [productCode, attributeCode]);
    return useSelectionStore(selector, shallow_1.shallow) || [];
};
/**
 * Maps the product options so they are easier to use, and adds a callback to each value that handles selecting each of them
 *
 * @param {string} productCode
 * @param {import('./selection.types.js').Attribute} attributeCode
 * @returns {import('./selection.types').ProductOption & {
 *  values: import('./selection.types').ProductOptionValue & { isSelected: boolean, selectValue: () => void },
 *  selectedValue: string,
 *  setAttributeValue: (value: string) => void
 * }}
 */
const useAttributeOptions = (productCode, attributeCode) => {
    const { [attributeCode]: currentAttributeValue } = useProductState(productCode, [attributeCode]);
    const attributeOptions = _useAttributeOptionsData(productCode, attributeCode);
    const updateProductAttributes = useUpdateProductAttributes();
    const hiddenAttributeValues = _useHiddenAttributeValues(productCode, attributeCode);
    if (!attributeOptions) {
        return null;
    }
    const updatedValues = attributeOptions.values.map((value) => {
        const isHidden = hiddenAttributeValues.includes(value.code);
        if (isHidden) {
            return null;
        }
        const isSelected = value.code === currentAttributeValue;
        const selectValue = () => updateProductAttributes(productCode, { [attributeCode]: value.code });
        return {
            ...value,
            isSelected,
            selectValue
        };
    }).filter(v => Boolean(v));
    return {
        ...attributeOptions,
        values: updatedValues,
        selectedValue: currentAttributeValue,
        selectedValueLabel: updatedValues.find(value => value.code === currentAttributeValue).label,
        setAttributeValue: (value) => updateProductAttributes(productCode, { [attributeCode]: value })
    };
};
exports.useAttributeOptions = useAttributeOptions;
/**
 * gets the price a single product after modifying one of it's attributes
 *
 * uses promotions hook under the hood
 * @param {string} productCode
 * @param {{
 *  [import('./selection.types.js').Attribute]: any
 * }} updatedAttributes
 * @returns {({
 *  sku: string,
 *  fullPrice: number,
 *  finalPrice: number
 * })}
 */
const useProductPricing = (productCode, updatedAttributes = {}) => {
    const { getDiscounts } = (0, pattern_library_modules_promotions_1.useGetDiscounts)();
    const productData = useProductData(productCode);
    const { variants, configurableAttributes } = productData || {};
    const currentProductState = useProductState(productCode, configurableAttributes);
    if (!productData || !currentProductState) {
        return {};
    }
    const newProductAttributes = (0, selection_utils_1.mergeProductAttributes)(currentProductState, updatedAttributes);
    // find product variant that matchs new state
    const newProductVariant = (0, selection_utils_1.findMatchingVariant)(variants, newProductAttributes, configurableAttributes);
    if (!newProductVariant) {
        console.log('no matching variant found!');
        return {};
    }
    const { sku, category, price } = newProductVariant;
    const selectedProducts = [{
            sku,
            parentSku: productCode,
            category,
            quantity: 1,
            price
        }];
    const { fullPrice, finalPrice } = getDiscounts(selectedProducts);
    return { fullPrice, finalPrice, sku };
};
exports.useProductPricing = useProductPricing;
/**
 * gets the cost of the products and attachments to be used when calculating subtotals
 *
 * Uses promotions hook under the hood
 * @param {string[]} productCodes
 * @param {Array<import('./selection.types.js').Attribute>} attributes
 * @returns {({
 *  fullPrice: number,
 * finalPrice: number
 * })}
 */
const useSelectionPricing = (productCodes, attributes = ['productCode', 'sku', 'price', 'quantity', 'inStock', 'category']) => {
    const { getDiscounts } = (0, pattern_library_modules_promotions_1.useGetDiscounts)();
    const currentProductsState = useProductsState(productCodes, attributes);
    const selectedProducts = currentProductsState.map((productState) => {
        if (!productState) {
            return {};
        }
        const { sku, price, quantity, productCode, category, inStock } = productState;
        return {
            sku,
            parentSku: productCode,
            category: category.toLowerCase(),
            quantity: inStock ? quantity : 0,
            price
        };
    });
    const { fullPrice, finalPrice } = getDiscounts(selectedProducts);
    return { fullPrice, finalPrice };
};
exports.useSelectionPricing = useSelectionPricing;
/**
 *
 * @param {string[]} productCode
 * @param {Array<import('./selection.types.js').Attribute>} attributes
 * @returns {({
 *  inStock: boolean,
 *  item: {
 *      sku: string,
 *      type: string,
 *      quantity: number,
 *      bundleProductOptions: any[]
 *      category: string,
 *      parentSku: string,
 *      price: number,
 *      label: string
 *  }
 * })}
 */
const useAddToCartData = (productCode, attributes = ['sku', 'quantity', 'magentoBundleOptions', 'inStock', 'category', 'price']) => {
    const productState = useProductState(productCode, attributes);
    const productData = useProductData(productCode);
    if (!productData || !productState) {
        return {
            inStock: false,
            items: []
        };
    }
    const { sku, quantity, magentoBundleOptions, inStock, category, price } = productState;
    const { name } = productData;
    // we should surface this from the product data
    const type = magentoBundleOptions ? 'bundle' : 'simple';
    return {
        inStock,
        items: [
            {
                sku,
                type,
                quantity: parseInt(quantity),
                bundleProductOptions: [magentoBundleOptions],
                category: category && category.toLowerCase(),
                parentSku: productCode,
                price,
                label: name
            }
        ]
    };
};
exports.useAddToCartData = useAddToCartData;
/**
 * The attributes passed in are what are returned at the end, the return statement is the default set for this function
 * @param {string[]} productCodes
 * @param {Array<import('./selection.types.js').Attribute>} attributes
 * @returns {({
 *  quantityOfProducts: number,
 *  item: {
 *      inStock: boolean,
 *      sku: string,
 *      type: string,
 *      color: string,
 *      size: string,
 *      quantity: number,
 *      bundleProductOptions: any[]
 *      category: string,
 *      parentSku: string,
 *      price: number,
 *      label: string
 *  }
 * })}
 */
const useAddProductsToCart = (productCodes, attributes = ['sku', 'quantity', 'magentoBundleOptions', 'inStock', 'category', 'price', 'name', 'color', 'size', 'productCode']) => {
    const productsState = useProductsState(productCodes, attributes);
    if (!productsState) {
        return {
            items: []
        };
    }
    return (0, selection_utils_1.getVariantDataForCart)(productsState);
};
exports.useAddProductsToCart = useAddProductsToCart;
/**
 * Using the product code, tries to find the corresponding variant that matches
 * the attributeCode (option code) and the specified value
 * @param {string} productCode
 * @param {import('./selection.types').Attribute} attribute
 * @param {string} attributeValue
 * @returns {import('./selection.types').Variant | null}
 */
const useFindMatchingVariant = (productCode, attribute, attributeValue) => {
    // Product data contains all variants regardless of selection
    const productData = useProductData(productCode);
    // We use the current variant to ensure we match all the other attributes of the current selection
    const currentVariant = useProductState(productCode);
    const filterVariants = productData && productData.variants.filter(variant => {
        return variant[attribute] === attributeValue;
    }) || [];
    if (!filterVariants.length)
        return null;
    const matchingAttributes = productData.configurableAttributes.filter(item => item !== attribute);
    return (0, selection_utils_1.findMatchingVariant)(filterVariants, currentVariant, matchingAttributes);
};
exports.useFindMatchingVariant = useFindMatchingVariant;
/**
 * For a given product code, find all variants of the provided attribute,
 * with all other attributes values matching the currently selected product.
 * For example:
 *      Give me all `color` variants of this product where all
 *      other attributes match the currently selected product.
 * @param {string} productCode
 * @param {import('./selection.types').Attribute} attribute
 * @returns
 */
const useFindVariantsOfAttribute = (productCode, attribute) => {
    // Product data contains all variants regardless of selection
    const productData = useProductData(productCode);
    // We use the current variant to ensure we match all the other attributes of the current selection
    const currentVariant = useProductState(productCode);
    // Find all variants that match all attributes except the provided one
    const matchingAttributes = productData.configurableAttributes.filter(item => item !== attribute);
    return (0, selection_utils_1.findMatchingVariants)(productData.variants, currentVariant, matchingAttributes);
};
exports.useFindVariantsOfAttribute = useFindVariantsOfAttribute;
//# sourceMappingURL=selection.hooks.js.map